import React, { Component } from "react";
import { FormattedDate, FormattedMessage, FormattedTime, injectIntl } from "react-intl";
import { ADMIN, UnitTypes } from "../../../common/constant";
import {
  CREATE_NEW_USER_DIALOG,
  EDIT_USER_DIALOG,
  FA_TARGET_NAME,
  SEND_USER_NOTIFICATION,
  USER_PARTICIPATIONS
} from "../../../ducks/userManagement";

import { faChevronDown, faChevronUp, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import { ButtonGroup, Card, Col, Collapse, Form, FormLabel, FormSelect, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import Modal from "react-bootstrap/Modal";
import ModalDialogUtil from "../../../utils/ModalDialogUtil";
import Checkbox from "../../common/Checkbox";
import Table from "../../common/Table";
import FundingArea from "../fundingArea/FundingAreaContainer";
import { resolveRoleDisplayName } from "../roleManagement/RoleNameResolver";

const initialState = {
  selectedRoleName: undefined,
  selectedUnit: undefined,
  showRoleAssignments: false,
  showEffectiveRoles: false,
  showPermissionHistory: false
};

const unitTypesToRoleScopes = {
  [UnitTypes.FUNDING_AREA]: "fundingarea",
  [UnitTypes.PROGRAM]: "program",
  [UnitTypes.PROGRAM_INSTANCE]: "programInstance",
  [UnitTypes.PROJECT]: "project"
};

const FORM_COL_SM = 3;
const FORM_COL_OFFSET = 9;

const resolveHistoryDate = (occurredAt) => {
  if (typeof occurredAt === "number") {
    // Jackson versions may serialize Instant as epoch seconds. JavaScript Date
    // expects epoch milliseconds.
    return new Date(Math.abs(occurredAt) < 100000000000 ? occurredAt * 1000 : occurredAt);
  }
  return new Date(occurredAt);
};

class CreateEditUserDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ...initialState,
      showRoleAssignments: !!props.createNewUserDialog
    };
  }

  debouncePropertyChange = debounce(this.props.propertyChange, 700);

  onChangePropertyLazy = (e) => {
    this.debouncePropertyChange(e.target.name, e.target.value.trim());
  };

  componentDidUpdate(previousProps) {
    if (this.props.createNewUserDialog && !previousProps.createNewUserDialog) {
      this.setState({
        showRoleAssignments: true,
        showEffectiveRoles: false,
        showPermissionHistory: false
      });
    } else if (
      this.props.editUserDialog &&
      (!previousProps.editUserDialog || previousProps.username !== this.props.username)
    ) {
      this.setState({
        showRoleAssignments: false,
        showEffectiveRoles: false,
        showPermissionHistory: false
      });
    }
  }

  togglePermissionHistory = () => {
    const showPermissionHistory = !this.state.showPermissionHistory;
    this.setState({ showPermissionHistory });

    if (showPermissionHistory && this.props.username) {
      this.props.loadUserPermissionHistory(this.props.username);
    }
  };

  addParticipation = () => {
    this.props.propertyChange(USER_PARTICIPATIONS, [
      ...this.props.userParticipations,
      {
        role: this.state.selectedRoleName,
        unit: this.state.selectedUnit
      }
    ]);
    this.setState({
      selectedRoleName: undefined,
      selectedUnit: undefined
    });
    this.props.setFilters(FA_TARGET_NAME, null, null, null);
  };

  removeParticipation = (participation) => {
    this.props.propertyChange(
      USER_PARTICIPATIONS,
      this.props.userParticipations.filter(
        (p) =>
          p.role !== participation.role ||
          p.unit.type !== participation.unit.type ||
          p.unit.id !== participation.unit.id
      )
    );
  };

  render() {
    const {
      intl,
      username,
      firstname,
      lastname,
      employeeNumber,
      active,
      propertyChange,
      createNewUserDialog,
      editUserDialog,
      currentUsernameIsValid,
      userAlreadyExists,
      editUser,
      createNewUser,
      user_roles,
      fundingAreaList,
      roleList,
      userParticipations,
      locale,
      roles,
      sendUserNotification,
      userPermissionHistory,
      userPermissionHistoryLoading
    } = this.props;

    const dialog = createNewUserDialog ? CREATE_NEW_USER_DIALOG : EDIT_USER_DIALOG;

    const userParticipationsCopy = userParticipations ? [...userParticipations] : [];

    const handleCancelDialog = (dialog, show) => {
      ModalDialogUtil.closeModalDialogBodyFix();
      propertyChange(dialog, show);
    };

    const formIsValid =
      firstname?.trim().length > 0 &&
      lastname?.trim().length > 0 &&
      username?.trim().length > 0 &&
      userParticipations?.length > 0;

    let filteredRoles = [];

    if (roleList && this.state.selectedUnit) {
      filteredRoles = roleList.filter(
        (role) => !role.scope || role.scope === unitTypesToRoleScopes[this.state.selectedUnit.type]
      );
    }

    let roleSelectDefaultI18nKey = "user.management.choose.role";
    let roleChooserDisabled = false;

    if (!this.state.selectedUnit) {
      roleSelectDefaultI18nKey = "user.management.please.choose.a.unit";
      roleChooserDisabled = true;
    } else if (
      !roles.includes(ADMIN) &&
      (!this.state.selectedUnit || this.state.selectedUnit.type !== UnitTypes.PROJECT)
    ) {
      roleSelectDefaultI18nKey = "user.management.please.choose.a.project";
      roleChooserDisabled = true;
    }

    const resolveHistoryRole = (roleName) => {
      if (!roleName) {
        return "";
      }
      const role = roleList.find((candidate) => candidate.name === roleName);
      return role ? resolveRoleDisplayName(role, locale, intl) : roleName;
    };

    return (
      <Modal show={createNewUserDialog || editUserDialog} size="xl" scrollable>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id={`user.management.${editUserDialog ? "edit" : "create.new"}.user`} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <FormGroup className="mb-3" as={Row}>
              <FormLabel column sm={FORM_COL_SM}>
                <FormattedMessage id="user.management.table.username" /> *
              </FormLabel>
              <Col sm={FORM_COL_OFFSET}>
                <FormControl
                  required
                  disabled={editUserDialog}
                  name="username"
                  defaultValue={username || ""}
                  isInvalid={username?.trim() === "" || userAlreadyExists || !currentUsernameIsValid}
                  onBlur={(e) => this.onChangePropertyLazy(e)}
                />
                {username?.trim() === "" && (
                  <FormControl.Feedback type="invalid">
                    <FormattedMessage id="app.common.field.error.required" />
                  </FormControl.Feedback>
                )}
                {userAlreadyExists && (
                  <FormControl.Feedback type="invalid">
                    <FormattedMessage id="user.management.create.new.user.exists.warning" />
                  </FormControl.Feedback>
                )}
                {!currentUsernameIsValid && (
                  <FormControl.Feedback type="invalid">
                    <FormattedMessage id="user.management.create.new.user.username.warning" />
                  </FormControl.Feedback>
                )}
              </Col>
            </FormGroup>
            <FormGroup className="mb-3" as={Row}>
              <FormLabel column sm={FORM_COL_SM}>
                <FormattedMessage id="user.management.table.firstname" /> *
              </FormLabel>
              <Col sm={FORM_COL_OFFSET}>
                <FormControl
                  required
                  name="firstname"
                  isInvalid={firstname?.trim() === ""}
                  defaultValue={firstname || ""}
                  onBlur={(e) => this.props.propertyChange(e.target.name, e.target.value.trim())}
                  autoComplete="off"
                />
                <FormControl.Feedback type="invalid">
                  <FormattedMessage id="app.common.field.error.required" />
                </FormControl.Feedback>
              </Col>
            </FormGroup>
            <FormGroup className="mb-3" as={Row}>
              <FormLabel column sm={FORM_COL_SM}>
                <FormattedMessage id="user.management.table.lastname" />*
              </FormLabel>
              <Col sm={FORM_COL_OFFSET}>
                <FormControl
                  required
                  name="lastname"
                  isInvalid={lastname?.trim() === ""}
                  defaultValue={lastname || ""}
                  onBlur={(e) => this.props.propertyChange(e.target.name, e.target.value.trim())}
                  autoComplete="off"
                />
                <FormControl.Feedback type="invalid">
                  <FormattedMessage id="app.common.field.error.required" />
                </FormControl.Feedback>
              </Col>
            </FormGroup>
             <FormGroup className="mb-3" as={Row}>
              <FormLabel column sm={FORM_COL_SM}>
                <FormattedMessage id="user.management.table.employeeNumber" />
              </FormLabel>
              <Col sm={FORM_COL_OFFSET}>
                <FormControl
                  required={false}
                  name="employeeNumber"
                  disabled={editUserDialog}
                  //isInvalid={employeeNumber?.trim() === ""}
                  defaultValue={employeeNumber || ""}
                  onBlur={(e) => this.props.propertyChange(e.target.name, e.target.value.trim())}
                  autoComplete="off"
                />
                <FormControl.Feedback type="invalid">
                  <FormattedMessage id="app.common.field.error.required" />
                </FormControl.Feedback>
              </Col>
            </FormGroup>
            {editUserDialog && (
              <FormGroup className="mb-3" as={Row}>
                <FormLabel column sm={FORM_COL_SM}>
                  <FormattedMessage id="user.management.table.active" />
                </FormLabel>
                <Col sm={FORM_COL_OFFSET}>
                  <Checkbox
                    name="active"
                    defaultChecked={active}
                    onChange={(e) => this.props.propertyChange(e.target.name, e.target.checked)}
                  />
                </Col>
              </FormGroup>
            )}
            <FormGroup className="mb-3" as={Row}>
              <FormLabel column sm={FORM_COL_SM}>
                <FormattedMessage id="manage.survey.user.send.user.notification" />
              </FormLabel>
              <Col sm={FORM_COL_OFFSET}>
                <Checkbox
                  name={SEND_USER_NOTIFICATION}
                  defaultChecked={sendUserNotification}
                  disabled
                  onChange={(e) => this.props.propertyChange(e.target.name, !sendUserNotification)}
                />
              </Col>
            </FormGroup>
            <Card className="mb-2">
              <Card.Header>
                <Card.Title className="m-0">
                  <Button
                    type="button"
                    variant="link"
                    className="me-2"
                    title={intl.formatMessage({ id: "user.management.role.assignments" })}
                    aria-expanded={this.state.showRoleAssignments}
                    onClick={() => this.setState({ showRoleAssignments: !this.state.showRoleAssignments })}
                  >
                    <FontAwesomeIcon icon={this.state.showRoleAssignments ? faChevronUp : faChevronDown} />
                  </Button>
                  <FormattedMessage id="user.management.role.assignments" />
                </Card.Title>
              </Card.Header>
              <Collapse in={this.state.showRoleAssignments}>
                <div>
                  <Card.Body>
                    <FormGroup className="mb-3">
                      <FormLabel>
                        <FormattedMessage id="role.management.role.scope" />
                      </FormLabel>
                      <FundingArea
                        target={FA_TARGET_NAME}
                        activeOnly={true}
                        fundingAreaList={fundingAreaList}
                        onSelectionStateChange={(name, unit) => this.setState({ selectedUnit: unit })}
                      />
                    </FormGroup>
                    <FormGroup className="mb-3">
                      <FormLabel>
                        <FormattedMessage id="user.management.choose.role" />
                      </FormLabel>
                      <FormSelect
                        disabled={roleChooserDisabled}
                        placeholder={intl.formatMessage({ id: "user.surveyBuilder.fundingArea" })}
                        onChange={(event) => this.setState({ selectedRoleName: event.target.value })}
                        value={this.state.selectedRoleName || ""}
                      >
                        <option value="">{intl.formatMessage({ id: roleSelectDefaultI18nKey })}</option>
                        {filteredRoles?.map((role) => (
                          <option value={role.name} key={`role${role.id}`}>
                            {resolveRoleDisplayName(role, locale, intl)}
                          </option>
                        ))}
                      </FormSelect>
                    </FormGroup>
                    <ButtonGroup className="w-100">
                      <Button
                        variant="success"
                        onClick={this.addParticipation}
                        disabled={!this.state.selectedRoleName || !this.state.selectedUnit}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        <FormattedMessage id="app.common.action.add" />
                      </Button>
                    </ButtonGroup>
                  </Card.Body>
                  {userParticipationsCopy.length > 0 && (
                    <Card.Footer>
                      <Table
                        borderless
                        striped
                        columns={[
                          {
                            name: "unit",
                            labelKey: "app.common.organizational.unit",
                            customBodyRenderer: (participation) => this.resolveUnitDisplayName(participation.unit)
                          },
                          {
                            name: "role",
                            labelKey: "app.common.role",
                            customBodyRenderer: (participation) => {
                              const resolvedRole = roleList.find((r) => r.name === participation.role);
                              return resolvedRole ? resolveRoleDisplayName(resolvedRole, locale, intl) : participation.role;
                            }
                          },
                          {
                            name: "actions",
                            actions: [
                              {
                                code: "delete",
                                variant: "danger",
                                labelKey: "app.common.action.delete",
                                icon: faTrash,
                                onClick: (participation) => this.removeParticipation(participation),
                                hidden: (participation) => participation.id === "NEWPARTICIPATION"
                              }
                            ]
                          }
                        ]}
                        data={userParticipationsCopy}
                      />
                    </Card.Footer>
                  )}
                </div>
              </Collapse>
            </Card>
            {user_roles && user_roles.length > 0 && (
              <Card className="mb-2">
                <Card.Header>
                  <Card.Title className="m-0">
                    <Button
                      type="button"
                      variant="link"
                      className="me-2"
                      title={intl.formatMessage({ id: "user.management.edit.effective.permissions" })}
                      aria-expanded={this.state.showEffectiveRoles}
                      onClick={() => this.setState({ showEffectiveRoles: !this.state.showEffectiveRoles })}
                    >
                      <FontAwesomeIcon icon={this.state.showEffectiveRoles ? faChevronUp : faChevronDown} />
                    </Button>
                    <FormattedMessage id="user.management.edit.effective.permissions" />
                  </Card.Title>
                </Card.Header>
                <Collapse in={this.state.showEffectiveRoles}>
                  <Card.Body className="p-0">
                    <Table
                      responsive
                      bordered
                      striped
                      className="mb-0"
                      columns={[
                        {
                          name: "role",
                          labelKey: "app.common.role",
                          customBodyRenderer: (role) => resolveRoleDisplayName(role, locale, intl)
                        },
                        {
                          name: "permissions",
                          labelKey: "app.common.permissions",
                          customBodyRenderer: (role) =>
                            role.permissions
                              ? role.permissions
                                  .map((permission) =>
                                    intl.formatMessage({ id: "user.management.roles." + permission.name })
                                  )
                                  .join(" ")
                              : ""
                        }
                      ]}
                      data={user_roles}
                    />
                  </Card.Body>
                </Collapse>
              </Card>
            )}
            {editUserDialog && (
              <Card className="mb-2">
                <Card.Header>
                  <Card.Title className="m-0">
                    <Button
                      type="button"
                      variant="link"
                      className="me-2"
                      title={intl.formatMessage({ id: "user.management.history.title" })}
                      aria-expanded={this.state.showPermissionHistory}
                      onClick={this.togglePermissionHistory}
                    >
                      <FontAwesomeIcon icon={this.state.showPermissionHistory ? faChevronUp : faChevronDown} />
                    </Button>
                    <FormattedMessage id="user.management.history.title" />
                  </Card.Title>
                </Card.Header>
                <Collapse in={this.state.showPermissionHistory}>
                  <div>
                    {userPermissionHistoryLoading ? (
                      <Card.Body>
                        <FormattedMessage id="user.management.history.loading" />
                      </Card.Body>
                    ) : userPermissionHistory?.length > 0 ? (
                      <Card.Body className="p-0">
                        <Table
                          responsive
                          striped
                          className="mb-0"
                          columns={[
                            {
                              name: "occurredAt",
                              labelKey: "user.management.history.date",
                              customBodyRenderer: (entry) => {
                                const occurredAt = resolveHistoryDate(entry.occurredAt);
                                return (
                                  <>
                                    <FormattedDate value={occurredAt} year="numeric" month="2-digit" day="2-digit" />{" "}
                                    <FormattedTime value={occurredAt} hour="2-digit" minute="2-digit" />
                                  </>
                                );
                              }
                            },
                            {
                              name: "changeType",
                              labelKey: "user.management.history.change",
                              customBodyRenderer: (entry) => (
                                <FormattedMessage id={`user.management.history.change.${entry.changeType}`} />
                              )
                            },
                            {
                              name: "unitName",
                              labelKey: "app.common.organizational.unit",
                              customBodyRenderer: (entry) =>
                                entry.unitForeignId ? `${entry.unitName || ""} (${entry.unitForeignId})` : entry.unitName
                            },
                            {
                              name: "role",
                              labelKey: "app.common.role",
                              customBodyRenderer: (entry) =>
                                entry.changeType === "ROLE_CHANGED"
                                  ? `${resolveHistoryRole(entry.oldRole)} → ${resolveHistoryRole(entry.newRole)}`
                                  : resolveHistoryRole(entry.newRole || entry.oldRole)
                            },
                            {
                              name: "source",
                              labelKey: "user.management.history.source",
                              customBodyRenderer: (entry) => (
                                <FormattedMessage id={`user.management.history.source.${entry.source}`} />
                              )
                            },
                            {
                              name: "actor",
                              labelKey: "user.management.history.actor"
                            }
                          ]}
                          data={userPermissionHistory}
                        />
                      </Card.Body>
                    ) : (
                      <Card.Body>
                        <FormattedMessage id="user.management.history.empty" />
                      </Card.Body>
                    )}
                  </div>
                </Collapse>
              </Card>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleCancelDialog(dialog, false)}>
            <FormattedMessage id="user.management.create.new.user.cancel" />
          </Button>
          {createNewUserDialog ? (
            <Button onClick={() => createNewUser()} disabled={!formIsValid}>
              <FormattedMessage id="user.management.create.new.user.save" />
            </Button>
          ) : editUserDialog ? (
            <Button onClick={editUser} disabled={!formIsValid}>
              <FormattedMessage id="user.management.create.new.user.save" />
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    );
  }

  handleDrop = (event) => {
    const dragData = JSON.parse(event.dataTransfer.getData("text"));
    const { moveRole } = this.props;

    if (dragData.type === "moveRole") {
      moveRole(dragData.role, dragData.activate);
      event.stopPropagation();
      event.preventDefault();
    }
  };

  resolveUnitDisplayName = (unit) => {
  let name;

  switch (unit.type) {
    case UnitTypes.FUNDING_AREA:
      name = unit.name;
      break;

    case UnitTypes.PROGRAM:
      name = !unit.fundingArea
        ? `${unit.name} (PG)`
        : `${unit.fundingArea.name} / ${unit.name}`;
      break;

    case UnitTypes.PROGRAM_INSTANCE:
      name = !unit.program
        ? `${unit.name} (FPI)`
        : `${unit.program.fundingArea?.name} / ${unit.program.name} / ${unit.name}`;
      break;

    case UnitTypes.PROJECT:
      name = !unit.programInstance
        ? `${unit.name} (PR)`
        : `${unit.programInstance.program?.fundingArea?.name} / `
          + `${unit.programInstance.program?.name} / `
          + `${unit.programInstance.name} / `
          + `${unit.name}`;
      break;

    default:
      name = unit.name;
  }

  // Append the foreignId if present
  return unit.foreignId ? `${name} (${unit.foreignId})` : name;
};

  handleDragStart = (role, activate, event) => {
    const dragData = JSON.stringify({
      type: "moveRole",
      role: role,
      activate: activate
    });

    event.dataTransfer.setData("text", dragData);
  };
}

CreateEditUserDialog.propTypes = {
  propertyChange: PropTypes.func,
  editUser: PropTypes.func,
  createNewUser: PropTypes.func,
  createNewUserDialog: PropTypes.bool,
  editUserDialog: PropTypes.bool,
  username: PropTypes.string,
  firstname: PropTypes.string,
  lastname: PropTypes.string,
  password: PropTypes.string,
  active: PropTypes.bool,
  user_roles: PropTypes.array,
  /** roles of current user*/
  roles: PropTypes.array,
  currentPasswordIsValid: PropTypes.bool,
  currentUsernameIsValid: PropTypes.bool,
  userAlreadyExists: PropTypes.bool,
  localRoles: PropTypes.array,
  fundingAreaList: PropTypes.array,
  roleList: PropTypes.array,
  userParticipations: PropTypes.array,
  locale: PropTypes.string.isRequired,
  setFilters: PropTypes.func.isRequired,
  sendUserNotification: PropTypes.bool,
  userPermissionHistory: PropTypes.array,
  userPermissionHistoryLoading: PropTypes.bool,
  loadUserPermissionHistory: PropTypes.func
};

export default injectIntl(CreateEditUserDialog);
